import {
    AlertCircle,
    Award,
    CheckCircle,
    ChevronDown,
    ExternalLink,
    Globe,
    Grid3X3,
    Lock,
    Mail,
    Phone,
    Save,
    X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Liste des avatars disponibles dans le dossier public/data/avatars
const AVAILABLE_AVATARS = [
    '/data/avatars/avatar-1.png',
    '/data/avatars/avatar-2.png',
    '/data/avatars/avatar-3.png',
    '/data/avatars/avatar-4.png',
    '/data/avatars/avatar-5.png',
    '/data/avatars/avatar-6.png',
];

// Liste des professions
const PROFESSIONS = [
    'Administrateur de biens',
    'Agent immobilier',
    'Architecte',
    'Artisan',
    'Avocat spécialisé immobilier',
    'Chasseur immobilier',
    'Conseiller en gestion de patrimoine',
    'Consultant en investissement locatif',
    'Courtier en crédit immobilier',
    'Diagnostiqueur immobilier',
    'Directeur d\'agence immobilière',
    'Entrepreneur en bâtiment',
    'Expert immobilier',
    'Géomètre-expert',
    'Gestionnaire de patrimoine immobilier',
    'Home stager',
    'Investisseur immobilier',
    'Maître d\'œuvre',
    'Marchand de biens',
    'Négociateur immobilier',
    'Notaire',
    'Promoteur immobilier',
    'Responsable commercial immobilier',
    'Syndic de copropriété',
    'Autre',
];

const PersonalInfoCard = ({
    // Données reçues du parent
    contactData,
    loading = false,
    error = null,
    saving = false,
    
    // Handlers pour les modifications
    onContactChange,
    onContactBulkChange,
    onVisibilityChange,
    
    // Props UI existantes
    isProfileVisible = false,
    similarProfilesCount = 0,
    onFindSimilarProfiles,
    isSubscribed = false
}) => {
    // États locaux pour l'UI seulement (pas de données métier)
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);
    const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);
    const [professionSearch, setProfessionSearch] = useState('');
    
    // Refs pour la gestion des clics extérieurs
    const avatarSelectorRef = useRef(null);
    const professionDropdownRef = useRef(null);

    // Badges de certification
    const badges = [
        {
            name: 'Complet',
            icon: CheckCircle,
            active: contactData.prenom_contact && 
                   contactData.nom_contact && 
                   contactData.profession && 
                   contactData.email_contact && 
                   contactData.telephone_contact && 
                   contactData.url_pro
        },
        {
            name: 'Pro',
            icon: Award,
            active: false // sera relié à la base plus tard
        }
    ];

    // Fermer le sélecteur d'avatar en cliquant à l'extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (avatarSelectorRef.current && !avatarSelectorRef.current.contains(event.target)) {
                setShowAvatarSelector(false);
            }
        };

        if (showAvatarSelector) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showAvatarSelector]);

    // Fermer la liste déroulante des professions en cliquant à l'extérieur
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (professionDropdownRef.current && !professionDropdownRef.current.contains(event.target)) {
                setShowProfessionDropdown(false);
            }
        };

        if (showProfessionDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showProfessionDropdown]);

    // Filtrer les professions selon la recherche
    const filteredProfessions = PROFESSIONS.filter(profession =>
        profession.toLowerCase().includes(professionSearch.toLowerCase())
    );

    // Gérer les changements dans les champs
    const handleChange = (field, value) => {
        if (onContactChange) {
            onContactChange(field, value);
        }
    };

    // Sélection d'un avatar
    const handleAvatarSelect = (avatarPath) => {
        handleChange('photo_contact', avatarPath);
        setShowAvatarSelector(false);
    };

    // Sélection d'une profession
    const handleProfessionSelect = (profession) => {
        handleChange('profession', profession);
        setShowProfessionDropdown(false);
        setProfessionSearch('');
    };

    // Toggle visibilité du profil
    const handleVisibilityToggle = () => {
        const newVisibility = !contactData.is_visible;
        handleChange('is_visible', newVisibility);
        
        if (onVisibilityChange) {
            onVisibilityChange(newVisibility);
        }
    };

    // Affichage du loading
    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-full flex flex-col shadow-sm">
            {/* Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex-shrink-0">
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center text-gray-700">
                        <h2 className="text-sm font-semibold">Informations de Contact</h2>

                        {/* Indicateur de sauvegarde */}
                        {saving && (
                            <div className="ml-2 flex items-center text-blue-600">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                                <span className="text-xs">Sauvegarde...</span>
                            </div>
                        )}

                        {error && (
                            <div className="ml-2 flex items-center text-red-600">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                <span className="text-xs">Erreur</span>
                            </div>
                        )}
                    </div>

                    {/* Status visibilité */}
                    <div className="flex items-center space-x-1">
                        {contactData.is_visible ? (
                            <>
                                <Globe className="w-3 h-3 text-green-600" />
                                <span className="text-xs font-medium text-green-600">Public</span>
                            </>
                        ) : (
                            <>
                                <Lock className="w-3 h-3 text-gray-500" />
                                <span className="text-xs font-medium text-gray-500">Privé</span>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="flex-1 overflow-y-auto relative">
                <div className="p-4 pb-20">
                    {/* Affichage d'erreur */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center">
                                <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                                <span className="text-sm text-red-800">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Section Rendre Visible */}
                    <div className="absolute top-4 right-4 z-10">
                        <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-600">Rendre visible</span>
                            <button
                                onClick={handleVisibilityToggle}
                                className={`relative inline-flex items-center h-5 rounded-full w-9 transition-all duration-300 focus:outline-none shadow-inner ${contactData.is_visible
                                    ? ''
                                    : 'bg-gray-300'
                                    }`}
                                style={contactData.is_visible ? { background: "#d2e3ff" } : {}}
                            >
                                <span
                                    className={`inline-block w-3 h-3 transform rounded-full transition-transform duration-300 shadow-sm ${contactData.is_visible
                                        ? 'translate-x-5'
                                        : 'translate-x-1'
                                        }`}
                                    style={{ backgroundColor: contactData.is_visible ? "#0d71a5" : "white" }}
                                />
                            </button>
                        </div>
                    </div>

                    {/* Layout principal : Photo à gauche, Infos au centre */}
                    <div className="flex gap-6 mb-4 items-center">
                        {/* Grande photo à gauche */}
                        <div className="flex-shrink-0">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-2xl overflow-hidden border border-gray-200" style={{ backgroundColor: "transparent" }}>
                                    {contactData.photo_contact ? (
                                        <img src={contactData.photo_contact} alt="Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <span style={{ color: "#2596be" }}>
                                            {(contactData.prenom_contact?.[0] || '?')}{(contactData.nom_contact?.[0] || '?')}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setShowAvatarSelector(!showAvatarSelector)}
                                    className="absolute -bottom-1 -right-1 text-white p-2 bg-gray-300 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center hover:opacity-90"
                                    title="Choisir un avatar"
                                >
                                    <Grid3X3 className="w-4 h-4" style={{ color: "gray" }} />
                                </button>

                                {/* Sélecteur d'avatars */}
                                {showAvatarSelector && (
                                    <div
                                        ref={avatarSelectorRef}
                                        className="absolute top-0 left-28 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50 w-80"
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="text-sm font-semibold text-gray-800">Choisir un avatar</h3>
                                            <button
                                                onClick={() => setShowAvatarSelector(false)}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                                            {/* Option pour supprimer l'avatar */}
                                            <button
                                                onClick={() => handleAvatarSelect(null)}
                                                className={`w-16 h-16 rounded-full border-2 flex items-center justify-center text-lg font-bold ${!contactData.photo_contact
                                                    ? 'border-blue-500 ring-2 ring-blue-200'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                                style={{ backgroundColor: "transparent" }}
                                                title="Pas d'avatar"
                                            >
                                                <span style={{ color: "#016666" }}>
                                                    {(contactData.prenom_contact?.[0] || '?')}{(contactData.nom_contact?.[0] || '?')}
                                                </span>
                                            </button>

                                            {/* Avatars disponibles */}
                                            {AVAILABLE_AVATARS.map((avatarPath, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleAvatarSelect(avatarPath)}
                                                    className={`w-16 h-16 rounded-full border-2 overflow-hidden hover:scale-105 ${contactData.photo_contact === avatarPath
                                                        ? 'border-blue-500 ring-2 ring-blue-200'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                    style={{ backgroundColor: "transparent" }}
                                                    title={`Avatar ${index + 1}`}
                                                >
                                                    <img
                                                        src={avatarPath}
                                                        alt={`Avatar ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs hidden">
                                                        {index + 1}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>

                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <p className="text-xs text-gray-500 text-center">
                                                Sélectionnez un avatar pour votre profil professionnel
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Informations au centre */}
                        <div className="flex-1 min-w-0 mt-2">
                            {/* Nom et badges */}
                            <div className="mb-3">
                                <h3 className="text-sm font-bold text-gray-900 leading-tight mb-1">
                                    {contactData.prenom_contact || 'Prénom'} {contactData.nom_contact || 'Nom'}
                                </h3>
                                <p className="text-xs text-gray-500 mb-2">
                                    {contactData.profession || 'Professionnel Digital'}
                                </p>

                                {/* Badges */}
                                <div className="flex gap-1.5">
                                    {badges.map((badge, index) => {
                                        const IconComponent = badge.icon;
                                        return (
                                            <div
                                                key={index}
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200 ${badge.active
                                                    ? 'text-white border'
                                                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                                                    }`}
                                                style={badge.active ? {
                                                    backgroundColor: "#ffad65",
                                                    borderColor: "#ff9f43"
                                                } : {}}
                                            >
                                                <IconComponent className="w-2.5 h-2.5 mr-1" />
                                                {badge.name}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Formulaire */}
                    <div className="space-y-3">
                        {/* Prénom et Nom de contact */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Prénom</label>
                                <input
                                    type="text"
                                    value={contactData.prenom_contact || ''}
                                    onChange={(e) => handleChange('prenom_contact', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400"
                                    placeholder="Votre prénom"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Nom</label>
                                <input
                                    type="text"
                                    value={contactData.nom_contact || ''}
                                    onChange={(e) => handleChange('nom_contact', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400"
                                    placeholder="Votre nom"
                                />
                            </div>
                        </div>

                        {/* Profession */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Profession</label>
                            <div className="relative" ref={professionDropdownRef}>
                                <button
                                    type="button"
                                    onClick={() => setShowProfessionDropdown(!showProfessionDropdown)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400 text-left flex items-center justify-between"
                                >
                                    <span className={contactData.profession ? 'text-gray-900' : 'text-gray-500'}>
                                        {contactData.profession || 'Sélectionnez votre profession'}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showProfessionDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown des professions */}
                                {showProfessionDropdown && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden">
                                        {/* Champ de recherche */}
                                        <div className="p-3 border-b border-gray-100 bg-green-50">
                                            <input
                                                type="text"
                                                placeholder="Rechercher une profession..."
                                                value={professionSearch}
                                                onChange={(e) => setProfessionSearch(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white"
                                                autoFocus
                                            />
                                        </div>

                                        {/* Liste des professions */}
                                        <div className="max-h-48 overflow-y-auto">
                                            {filteredProfessions.map((profession, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleProfessionSelect(profession)}
                                                    className={`w-full px-4 py-2 text-left text-xs hover:bg-green-50 transition-colors duration-150 ${contactData.profession === profession
                                                        ? 'bg-green-100 text-green-800 font-medium'
                                                        : 'text-gray-700 hover:text-green-700'
                                                        }`}
                                                >
                                                    {profession}
                                                </button>
                                            ))}
                                            {filteredProfessions.length === 0 && (
                                                <div className="px-4 py-3 text-xs text-gray-500 text-center">
                                                    Aucune profession trouvée
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Email de contact */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Email de contact professionnel</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="email"
                                    value={contactData.email_contact || ''}
                                    onChange={(e) => handleChange('email_contact', e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400"
                                    placeholder="contact@exemple.com"
                                />
                            </div>
                        </div>

                        {/* Téléphone de contact */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Téléphone de contact professionnel</label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="tel"
                                    value={contactData.telephone_contact || ''}
                                    onChange={(e) => handleChange('telephone_contact', e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400"
                                    placeholder="+33 6 12 34 56 78"
                                />
                            </div>
                        </div>

                        {/* URL Pro */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">URL Pro (LinkedIn, IAD....)</label>
                            <div className="relative">
                                <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="url"
                                    value={contactData.url_pro || ''}
                                    onChange={(e) => handleChange('url_pro', e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 bg-white hover:border-gray-400"
                                    placeholder="https://linkedin.com/in/votre-profil"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Notice pour networking */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
                    <div className={`rounded-lg border p-3 ${contactData.is_visible
                        ? 'bg-gradient-to-r from-gray-50 to-gray-50 border-gray-100'
                        : 'bg-gradient-to-r from-gray-50 to-gray-50 border-gray-100'
                        }`}>
                        <div className="flex items-start space-x-2">
                            <div>
                                <p className="text-xs font-semibold text-gray-800">
                                    {contactData.is_visible ? 'Profil Public - Visible dans l\'espace réseaux' : 'Profil Privé'}
                                </p>
                                <p className="text-xs text-gray-700 leading-relaxed">
                                    {contactData.is_visible
                                        ? 'Votre profil est visible par les abonnés Selery pour des partenariats immobiliers.'
                                        : 'Rendez votre profil visible pour que les abonnés Selery puissent vous contacter et discuter de projets.'
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PersonalInfoCard;